import React, { useEffect, useState } from 'react';
import { Checkbox, Form, Input, Spin } from 'antd';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import moment from 'moment/moment.js';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal.jsx';
import Pagination from '../../../../components/pagination/Pagination.jsx';
import TableWithSkeleton from '../../../../components/table/TableWithSkeleton.jsx';
import columns from '../../../../columns/ibwallet.jsx';
import {
  GetIBWalletDetailsApi,
  WithdrawIBCommissionApi,
  GetIBWalletRequestListApi,
} from '../../../../api/request/ib/ibWallet';
import { showApiError } from '../../../../utils/utils.js';

export default function IBWithdrawMobile() {
  const [form] = Form.useForm();
  const [isWalletLoading, setIsWalletLoading] = useState(false);
  const [isWithdrawLoading, setIsWithdrawLoading] = useState(false);
  const [wallet, setWallet] = useState({});
  const [withdrawValues, setWithdrawValue] = useState(null);
  const [isOpenWithdrawConfirmationModal, setIsOpenWithdrawConfirmationModal] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setDataAll] = useState([]);
  const [sorter, setSorter] = useState({ SortBy: 'requestDate', SortOrder: '' });

  const getWalletDetails = async () => {
    try {
      setIsWalletLoading(true);
      const { data } = await GetIBWalletDetailsApi();
      if (data?.success) {
        setWallet(data?.data);
      }
    } catch (error) {
    } finally {
      setIsWalletLoading(false);
    }
  };

  const getIbWalletRequests = async () => {
    try {
      setIsLoading(true);
      const params = {
        PageNumber: page,
        PageSize: pageSize,
        ...sorter,
      };
      const { data: res } = await GetIBWalletRequestListApi(new URLSearchParams(params)?.toString());
      if (res?.success) {
        setDataAll(
          (res?.data || []).map((item) => ({
            ...item,
            name: `${item?.firstName || ''} ${item?.lastName || ''}`.trim(),
            requestDate: item?.requestDate ? moment(item.requestDate).format('DD-MM-YYYY LT') : null,
            amount: `$ ${item?.amount ?? 0}`,
          }))
        );
        setTotalRecord(res?.totalRecords || 0);
      } else {
        setDataAll([]);
        setTotalRecord(0);
      }
    } catch (error) {
      setDataAll([]);
      setTotalRecord(0);
    } finally {
      setIsLoading(false);
    }
  };

  const withdrawHandler = async () => {
    try {
      setIsWithdrawLoading(true);
      const { data } = await WithdrawIBCommissionApi(withdrawValues);
      if (data?.success) {
        toast.success(data?.message);
        form.resetFields();
        getWalletDetails();
        getIbWalletRequests();
        setIsOpenWithdrawConfirmationModal(false);
      }
    } catch (error) {
      showApiError(error);
    } finally {
      setIsWithdrawLoading(false);
    }
  };

  const handleSubmit = (values) => {
    setWithdrawValue({ amount: Number(values?.amount) });
    setIsOpenWithdrawConfirmationModal(true);
  };

  const handleTableChange = (pagination, filters, tableSorter) => {
    if (tableSorter?.order) {
      setSorter({ SortBy: tableSorter?.field, SortOrder: tableSorter?.order });
    } else {
      setSorter({ SortBy: 'requestDate', SortOrder: '' });
    }
  };

  useEffect(() => {
    getWalletDetails();
  }, []);

  useEffect(() => {
    getIbWalletRequests();
  }, [page, pageSize, sorter]);

  const balanceCards = [
    { label: 'Generate', value: wallet?.ibCommisionGenerate },
    { label: 'Available', value: wallet?.ibWalletAmount },
    { label: 'Withdrawn', value: wallet?.ibCommisionWithdraw },
    { label: 'Min / Max', value: `${wallet?.min || 0} / ${wallet?.max || 0}` },
  ];

  return (
    <div className="space-y-5">
      {isWalletLoading ? (
        <div className="flex justify-center flex-col my-8">
          <Spin size="large" />
          <p className="primary text-center mt-2">Loading...</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {balanceCards.map((card) => (
            <div key={card.label} className="bg-white rounded-2xl border border-gray-100 p-3 shadow-sm">
              <p className="text-xs text-gray-500">{card.label}</p>
              <p className="text-base font-semibold primary mt-1">$ {card.value || 0}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
        <p className="text-lg font-semibold text-gray-800 mb-3">Withdraw Commission</p>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="amount"
            label={<span className="text-sm font-medium text-gray-600">Amount In USD</span>}
            rules={[
              { required: true, message: 'Please enter amount.' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const amount = Number(value);
                  if (!Number.isInteger(amount)) {
                    return Promise.reject(new Error('Please enter a valid amount.'));
                  }
                  if (amount < (wallet?.minimum ?? wallet?.min)) {
                    return Promise.reject(new Error(`Amount must be at least $${wallet?.minimum ?? wallet?.min}.`));
                  }
                  if (amount > (wallet?.maximum ?? wallet?.max)) {
                    return Promise.reject(new Error(`Amount cannot exceed $${wallet?.maximum ?? wallet?.max}.`));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input type="number" placeholder="Amount in USD" size="large" className="py-2" />
          </Form.Item>

          <Form.Item
            name="conditions"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms and conditions.')),
              },
            ]}
          >
            <Checkbox>
              Yes, I agreed to the{' '}
              <Link to="/terms_&_conditions" className="primary hover:underline">
                Terms and conditions
              </Link>
            </Checkbox>
          </Form.Item>

          <button
            type="submit"
            className="w-full bg-primary text-white font-semibold rounded-lg px-4 py-3 uppercase themeHover duration-500"
          >
            Submit
          </button>
        </Form>
      </div>

      <div>
        <p className="text-lg font-semibold text-gray-800 mb-3">Withdraw Requests</p>
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <TableWithSkeleton columns={columns} data={data} loading={isLoading} onChange={handleTableChange} />
          </div>
        </div>
        <div className="mt-4">
          <Pagination total={totalRecord} page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} />
        </div>
      </div>

      <ConfirmationModal
        isOpen={isOpenWithdrawConfirmationModal}
        setIsOpen={setIsOpenWithdrawConfirmationModal}
        type="success"
        message="Are you sure you want to proceed this withdraw request?"
        onConfirm={withdrawHandler}
        isLoading={isWithdrawLoading}
        loadingLabel="Adding request..."
      />
    </div>
  );
}
